<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5"><strong>You will need:</strong> A tape measure and a pen and paper</p>
    <p class="mb-5">
      There’s an old saying in the building game: <strong>“Measure twice, cut once!”</strong>
    </p>
    <p class="mb-5">
      Being able to measure accurately is an essential skill for anyone who works in construction.
      In this activity, you’ll get some practice both estimating (guessing) and measuring distances.
    </p>
    <h4 class="mb-5">Units of Measurement</h4>
    <p class="mb-5">
      The first thing we need to know about measuring things is which unit of measurement to use. In
      Australia, the metric system is used for all measurements in construction. This is the same as
      most of the world but not everywhere. For example, the United States still uses imperial
      measurements such as feet and inches.
    </p>
    <p class="mb-5">
      The most commonly used unit to measure in construction is the millimetre (mm), though metres
      (m) can be used for larger distances such as boundary lines. Centimetres (cm) are rarely used.
    </p>

    <centered-image-wrapper
      :image="require('@/assets/images/experiences/construction/tape_measure.jpg')"
      classes="xl:w-1/2 lg:w-2/3 w-full"
      alt=""
    />
    <h4 class="mb-5">Measurement Practice</h4>
    <p class="mb-5">
      1. Now, with your tape measure, practise measuring various distances both inside your house
      and outside. Measure the following distances of different parts of the built environment
      around you:
    </p>
    <ul class="default-list">
      <li>Length</li>
      <li>Width</li>
      <li>Depth</li>
      <li>Height</li>
    </ul>

    <p class="mb-5">
      2. After some practice, you should be able to start to make fairly accurate guesses or
      estimates of distances.
    </p>
    <p class="mb-5">
      Continue to find different things in the environment to measure, but first, write down an
      estimate. Then, measure to see how accurate you were. As you do this repeatedly, you should
      find your estimates get more accurate.
    </p>
    <p class="mb-5">
      Use the table below to record the object measured, the dimension (length, width, height,
      depth), the estimate, and the measurement.
    </p>
    <p class="mb-5">Some distances you could measure include:</p>
    <ul class="default-list">
      <li>The length and height of a wall or fence</li>
      <li>The length and width of a garden</li>
      <li>The height of a garage door</li>
      <li>The width of a window frame</li>
      <li>The depth of a hole</li>
    </ul>
    <h4 class="mb-5">Your Task:</h4>
    <p class="mb-5">
      Choose 9 different objects in the built environment around you. Complete the table below where
      for each object your enter:
    </p>
    <p class="mb-5">
      <strong>Object</strong> - the name of the thing to be measured e.g. gate, wall, fence, etc.
    </p>
    <p class="mb-5">
      <strong>Dimension</strong> – choose whether it is the Length, Width, Height, Depth
    </p>
    <p class="mb-5"><strong>Estimate</strong> - your guess of the measurement before measuring</p>
    <p class="mb-5">
      <strong>Measurement</strong> - actual measurement is taken using a tape measure
    </p>

    <table class="table default-table">
      <tr class="header">
        <th width="25%">Object</th>
        <th width="25%">Dimension</th>
        <th width="25%">Estimate</th>
        <th width="25%">Measurement</th>
      </tr>
      <tr class="body" v-for="index in 9" :key="index">
        <td><text-block v-model="data['question' + index + '_1']" :editable="isEditable" /></td>
        <td>
          <div class="vx-col w-full mb-2" v-if="isEditable">
            <select
              class="defaultselect m-0"
              v-model="data['question' + index + '_2']"
              style="width: 100%"
              aria-label="Measurement"
            >
              <option v-for="answer in answers" :key="answer.val" :value="answer.val">
                {{ answer.answer }}
              </option>
            </select>
          </div>
          <div class="my-2 mx-5" v-else>
            <p
              class="my-2"
              v-for="answer in answers"
              :key="answer.val"
              v-if="data['question' + index + '_2'] != 0"
            >
              <span v-if="data['question' + index + '_2'] == answer.val">{{ answer.answer }}</span>
            </p>
            <p class="my-2" v-else>N/A</p>
          </div>
        </td>
        <td><text-block v-model="data['question' + index + '_3']" :editable="isEditable" /></td>
        <td><text-block v-model="data['question' + index + '_4']" :editable="isEditable" /></td>
      </tr>
    </table>
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      answers: [
        { val: undefined, answer: 'Select dimension' },
        { val: 1, answer: 'Length' },
        { val: 2, answer: 'Width' },
        { val: 3, answer: 'Height' },
        { val: 4, answer: 'Depth' },
      ],
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
};
</script>

<style scoped>
.wrong {
  background: rgba(202, 20, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
  padding-top: 2px;
}
.correct {
  background: rgba(75, 202, 20, 0.24);
  border-bottom: 1px solid #f8f8f8;
}
.scale-header {
  background: #f5f5f5;
  padding-top: 5px;
  font-weight: 600;
}
</style>
